import { z } from 'zod';
import { ENCOUNTER_MODULES } from './modules';
import { MONEY_CAP_MINOR } from './money';
export const PERSON_AUTHORITY_KINDS = [
    'discount_pct',
    'discount_max_minor',
    'refund_approve_minor',
    'credit_note_approve_minor',
    'supplier_payment_approve_minor',
    'stock_writeoff_approve_minor',
    'pos_return_approve_minor',
    'claim_adjustment_approve_minor',
];
export const THRESHOLD_AUTHORITY_KINDS = [
    'supplier_payment_threshold_minor',
    'stock_writeoff_threshold_minor',
    'pos_return_threshold_minor',
];
export const SWITCH_AUTHORITY_KINDS = ['dual_control_authority', 'dual_control_share'];
export const AUTHORITY_KINDS = [...PERSON_AUTHORITY_KINDS, ...THRESHOLD_AUTHORITY_KINDS, ...SWITCH_AUTHORITY_KINDS];
export const AUTHORITY_GRANT_STATUS = ['pending', 'active', 'rejected', 'cancelled', 'replaced'];
export const AUTHORITY_SOURCE = ['manual', 'role_start', 'carried_over'];
export const DOCTOR_SHARE_SCOPES = ['service', 'doctor_category', 'doctor_service', 'ward_rule'];
export const SHAREABLE_CATEGORIES = ['consultation', 'procedure', 'ot', 'nursing', 'package', 'misc'];
export const CHARGE_CATEGORIES = ['consultation', 'procedure', 'bed', 'nursing', 'ot', 'lab', 'radiology', 'pharmacy', 'package', 'misc'];
export const MONEY_AUTHORITY_MODULES = ['billing', 'pharmacy', 'inventory'];
export const AUTHORITY_KIND_INFO = {
    discount_pct: {
        kind: 'discount_pct',
        subject: 'person',
        value: 'pct',
        unsetMeansNoLimit: false,
        switchPermissions: ['discount.apply', 'pharmacy.pos'],
        modules: ['billing', 'pharmacy'],
        label: 'Discount without approval (%)',
    },
    discount_max_minor: {
        kind: 'discount_max_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: ['discount.apply', 'pharmacy.pos'],
        modules: ['billing', 'pharmacy'],
        label: 'Largest discount on one bill',
    },
    refund_approve_minor: {
        kind: 'refund_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: false,
        switchPermissions: ['payment.refund.approve'],
        modules: ['billing'],
        label: 'Refund approval limit',
    },
    credit_note_approve_minor: {
        kind: 'credit_note_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: false,
        switchPermissions: ['payment.refund.approve'],
        modules: ['billing'],
        label: 'Credit note approval limit',
    },
    supplier_payment_approve_minor: {
        kind: 'supplier_payment_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: ['supplier.payment.approve'],
        modules: ['pharmacy', 'inventory'],
        label: 'Largest supplier payment they may approve',
    },
    stock_writeoff_approve_minor: {
        kind: 'stock_writeoff_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: ['stock.adjust.approve'],
        modules: ['pharmacy', 'inventory'],
        label: 'Largest write-off they may approve',
    },
    pos_return_approve_minor: {
        kind: 'pos_return_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: ['pharmacy.return.approve'],
        modules: ['pharmacy'],
        label: 'Largest counter return they may approve',
    },
    claim_adjustment_approve_minor: {
        kind: 'claim_adjustment_approve_minor',
        subject: 'person',
        value: 'minor',
        unsetMeansNoLimit: false,
        switchPermissions: ['claim.settle'],
        modules: ['insurance'],
        label: 'Claim settlement decisions they may take on one claim',
    },
    supplier_payment_threshold_minor: {
        kind: 'supplier_payment_threshold_minor',
        subject: 'hospital',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: [],
        modules: ['pharmacy', 'inventory'],
        label: 'Supplier payments above this need a second person',
    },
    stock_writeoff_threshold_minor: {
        kind: 'stock_writeoff_threshold_minor',
        subject: 'hospital',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: [],
        modules: ['pharmacy', 'inventory'],
        label: 'Write-offs above this need a second person',
    },
    pos_return_threshold_minor: {
        kind: 'pos_return_threshold_minor',
        subject: 'hospital',
        value: 'minor',
        unsetMeansNoLimit: true,
        switchPermissions: [],
        modules: ['pharmacy'],
        label: 'Counter returns above this need a supervisor',
    },
    dual_control_authority: {
        kind: 'dual_control_authority',
        subject: 'hospital',
        value: 'flag',
        unsetMeansNoLimit: false,
        switchPermissions: [],
        modules: ['billing', 'pharmacy', 'inventory'],
        label: 'Two-person rule for authority changes',
    },
    dual_control_share: {
        kind: 'dual_control_share',
        subject: 'hospital',
        value: 'flag',
        unsetMeansNoLimit: false,
        switchPermissions: [],
        modules: ['billing'],
        label: 'Two-person rule for doctor share changes',
    },
};
export function isPersonAuthorityKind(kind) {
    return PERSON_AUTHORITY_KINDS.includes(kind);
}
export function isThresholdAuthorityKind(kind) {
    return THRESHOLD_AUTHORITY_KINDS.includes(kind);
}
export function isSwitchAuthorityKind(kind) {
    return SWITCH_AUTHORITY_KINDS.includes(kind);
}
export function authorityKindAvailable(kind, modules) {
    return AUTHORITY_KIND_INFO[kind].modules.some((module) => modules.has(module));
}
export function doctorShareAvailable(modules) {
    return modules.has('billing') && ENCOUNTER_MODULES.some((module) => modules.has(module));
}
const reasonSchema = z.string().trim().min(5, 'Give a reason of at least 5 characters').max(400);
const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-01');
export const authorityChangeSchema = z
    .object({
    kind: z.enum(AUTHORITY_KINDS),
    userId: z.string().uuid().nullable().optional(),
    roleId: z.string().uuid().nullable().optional(),
    branchId: z.string().uuid().nullable().optional(),
    valuePct: z.number().min(0).max(100).nullable().optional(),
    valueMinor: z.number().int().min(0).max(9_000_000_000_000_000).nullable().optional(),
    noLimit: z.boolean().default(false),
    flag: z.boolean().nullable().optional(),
    reason: reasonSchema,
})
    .superRefine((value, ctx) => {
    const info = AUTHORITY_KIND_INFO[value.kind];
    if (info.subject === 'person' && Boolean(value.userId) === Boolean(value.roleId)) {
        ctx.addIssue({ code: 'custom', path: ['userId'], message: 'Choose one person or one role' });
    }
    if (info.subject === 'hospital' && (value.userId || value.roleId)) {
        ctx.addIssue({ code: 'custom', path: ['userId'], message: 'This setting applies to the hospital, not to a person' });
    }
    if (info.subject === 'person' && value.branchId) {
        ctx.addIssue({ code: 'custom', path: ['branchId'], message: 'A person limit applies in every branch' });
    }
    if (info.value === 'pct' && (value.valuePct === null || value.valuePct === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['valuePct'], message: 'Enter a percentage' });
    }
    if (info.value === 'minor' && !value.noLimit && (value.valueMinor === null || value.valueMinor === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['valueMinor'], message: 'Enter an amount, or choose no limit' });
    }
    if (info.value === 'minor' && value.noLimit && !info.unsetMeansNoLimit && info.subject === 'hospital') {
        ctx.addIssue({ code: 'custom', path: ['noLimit'], message: 'This setting needs an amount' });
    }
    if (info.value === 'flag' && typeof value.flag !== 'boolean') {
        ctx.addIssue({ code: 'custom', path: ['flag'], message: 'Choose on or off' });
    }
    if (info.value !== 'flag' && info.subject === 'hospital' && info.kind.startsWith('dual_control')) {
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'Unknown setting' });
    }
    if (info.value !== 'minor' && value.noLimit) {
        ctx.addIssue({ code: 'custom', path: ['noLimit'], message: 'No limit applies to amounts only' });
    }
});
export const authorityBulkChangeSchema = z.object({
    kind: z.enum(PERSON_AUTHORITY_KINDS),
    userIds: z.array(z.string().uuid()).min(1).max(100),
    valuePct: z.number().min(0).max(100).nullable().optional(),
    valueMinor: z.number().int().min(0).max(9_000_000_000_000_000).nullable().optional(),
    noLimit: z.boolean().default(false),
    reason: reasonSchema,
});
export const changeDecisionSchema = z.object({
    note: z.string().trim().max(400).optional(),
});
export const authorityPeopleQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    role: z.string().uuid().optional(),
    branch: z.string().uuid().optional(),
    handlesMoney: z.enum(['0', '1']).default('1'),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const authorityChangesQuerySchema = z.object({
    kind: z.enum(AUTHORITY_KINDS).optional(),
    status: z.enum(AUTHORITY_GRANT_STATUS).optional(),
    userId: z.string().uuid().optional(),
    source: z.enum(AUTHORITY_SOURCE).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const accountsReportQuerySchema = z.object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    branch: z.string().uuid().optional(),
    givenBy: z.string().uuid().optional(),
    approvedBy: z.string().uuid().optional(),
    kind: z.enum(['refund', 'credit_note', 'supplier_payment', 'stock_adjustment', 'pos_return']).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const doctorShareChangeSchema = z
    .object({
    scope: z.enum(DOCTOR_SHARE_SCOPES),
    chargeItemId: z.string().uuid().nullable().optional(),
    category: z.enum(CHARGE_CATEGORIES).nullable().optional(),
    doctorId: z.string().uuid().nullable().optional(),
    wardChargeRuleId: z.string().uuid().nullable().optional(),
    sharePct: z.number().min(0).max(100).nullable(),
    shareMinor: z.number().int().min(0).max(MONEY_CAP_MINOR).nullable().optional(),
    effectiveFrom: dateOnlySchema,
    reason: reasonSchema,
    reference: z.string().trim().max(120).optional(),
})
    .superRefine((value, ctx) => {
    const need = (key, ok) => {
        if (!ok)
            ctx.addIssue({ code: 'custom', path: [key], message: 'Required for this kind of share' });
    };
    const hasPct = value.sharePct !== null;
    const hasFixed = value.shareMinor !== null && value.shareMinor !== undefined;
    if (hasPct && hasFixed)
        ctx.addIssue({ code: 'custom', path: ['shareMinor'], message: 'Choose a percentage or a fixed amount, not both' });
    if (value.scope === 'service') {
        need('chargeItemId', Boolean(value.chargeItemId));
        if (!hasPct && !hasFixed)
            ctx.addIssue({ code: 'custom', path: ['sharePct'], message: 'A service default needs a percentage or a fixed amount' });
    }
    if (value.scope === 'doctor_category') {
        need('doctorId', Boolean(value.doctorId));
        need('category', Boolean(value.category));
    }
    if (value.scope === 'doctor_service') {
        need('doctorId', Boolean(value.doctorId));
        need('chargeItemId', Boolean(value.chargeItemId));
    }
    if (value.scope === 'ward_rule')
        need('wardChargeRuleId', Boolean(value.wardChargeRuleId));
});
export const categoryDefaultShareSchema = z.object({
    category: z.enum(CHARGE_CATEGORIES),
    sharePct: z.number().min(0).max(100),
    effectiveFrom: dateOnlySchema,
    reason: reasonSchema,
    reference: z.string().trim().max(120).optional(),
});
export const doctorShareServicesQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    category: z.enum(CHARGE_CATEGORIES).optional(),
    shareable: z.enum(['0', '1']).default('1'),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const doctorShareChangesQuerySchema = z.object({
    scope: z.enum(DOCTOR_SHARE_SCOPES).optional(),
    status: z.enum(AUTHORITY_GRANT_STATUS).optional(),
    doctorId: z.string().uuid().optional(),
    chargeItemId: z.string().uuid().optional(),
    source: z.enum(AUTHORITY_SOURCE).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const doctorShareExplainQuerySchema = z.object({
    doctorId: z.string().uuid(),
    chargeItemId: z.string().uuid(),
    date: dateOnlySchema.optional(),
    ruleId: z.string().uuid().optional(),
    branchId: z.string().uuid().optional(),
});
export const payoutAdjustmentSchema = z.object({
    amountMinor: z.number().int().min(1).max(9_000_000_000_000),
    reason: reasonSchema,
    reference: z.string().trim().max(120).optional(),
});
export const discountRequestDecisionSchema = z.object({
    note: z.string().trim().max(400).optional(),
});
export const discountRequestQuerySchema = z.object({
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const SUPERVISOR_AUTH_HEADER = 'x-supervisor-auth';
export function encodeSupervisorAuth(credentials) {
    const json = JSON.stringify(credentials);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
export function decodeSupervisorAuth(header) {
    if (!header || header.length > 2000)
        return null;
    try {
        const binary = atob(header);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const parsed = JSON.parse(new TextDecoder().decode(bytes));
        if (typeof parsed.username !== 'string' || typeof parsed.password !== 'string')
            return null;
        if (!parsed.username.trim() || !parsed.password)
            return null;
        return {
            username: parsed.username.slice(0, 200),
            password: parsed.password.slice(0, 200),
            ...(typeof parsed.totpCode === 'string' && parsed.totpCode ? { totpCode: parsed.totpCode.slice(0, 12) } : {}),
        };
    }
    catch {
        return null;
    }
}
export function csvCell(value) {
    if (value === null || value === undefined)
        return '';
    let text = value instanceof Date ? value.toISOString() : String(value);
    if (/^[=+\-@\t\r]/.test(text))
        text = `'${text}`;
    if (/[",\n\r]/.test(text))
        text = `"${text.replace(/"/g, '""')}"`;
    return text;
}
export function csvLine(values) {
    return `${values.map(csvCell).join(',')}\r\n`;
}
export const CSV_BOM = '﻿';
//# sourceMappingURL=accounts.js.map